/* SignWarehouse predictive search dropdown on Typesense, with Shopify native predictive search as fallback.
 *
 * Defines the <predictive-search> custom element BEFORE the theme's own predictive-search.js runs (that
 * file is appended after DOMContentLoaded and skips when the element already exists), so the header
 * markup (snippets/predictive-search.liquid) is untouched: same form, same input, same overlay, same
 * CSS. Only what fills .js-search-results changes.
 *
 * Fallback: when the search host does not answer within the configured timeout, or errors, the element
 * fetches Shopify's predictive search section exactly like the native script and renders that instead.
 * Both paths log the query (engine typesense|native) so the admin can see fallback rates.
 */
(function () {
  'use strict';
  if (typeof window === 'undefined' || !window.SWSearch || !window.SWSearch.configured() || !window.SWSearch.cfg.dropdown) return;
  if (customElements.get('predictive-search')) return;

  var S = window.SWSearch;
  var cfg = S.cfg;
  var str = cfg.strings || {};

  function debounce(fn, wait) {
    var t; return function () { var a = arguments, ctx = this; clearTimeout(t); t = setTimeout(function () { fn.apply(ctx, a); }, wait || 200); };
  }

  class SWPredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cache = {};
      this.seq = 0;
      this.currentQid = null;
      this.initialised = false;
    }

    /* Children may not be parsed yet when the element is upgraded during page load, so wire up on connect
       and, if the form is not there yet, again once the document has finished parsing. */
    connectedCallback() {
      if (this.initialised) return;
      if (!this.querySelector('.js-search-input') && document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.init.bind(this), { once: true });
        return;
      }
      this.init();
    }

    init() {
      if (this.initialised) return;
      this.input = this.querySelector('.js-search-input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.productTypeInput = document.getElementById('product_type_input');
      this.loadingText = this.getAttribute('data-loading-text') || 'Loading';
      if (!this.input || !this.results) return;
      this.initialised = true;
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this), 180));
      var form = this.querySelector('.search');
      if (form) form.addEventListener('submit', this.handleSubmit.bind(this));
      this.results.addEventListener('click', this.handleResultClick.bind(this));
      this.classList.add('sw-search-enabled');
    }

    disconnectedCallback() { if (this.initialised) this.close(); }

    getQuery() { return this.input.value.trim(); }

    handleSubmit(evt) {
      var q = this.getQuery();
      if (!q.length) { evt.preventDefault(); return; }
      var selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected && !selected.classList.contains('js-submit')) {
        evt.preventDefault();
        var link = selected.querySelector('.js-search-link');
        if (link) link.click();
      }
      // the results page logs its own query event
    }

    handleInput() {
      this.input.setAttribute('value', this.input.value);
      var q = this.getQuery();
      if (!q.length) { this.close(); return; }
      this.getResults(q);
    }

    handleFocus() {
      S.warm(); // open the TLS connection before the first keystroke
      var q = this.getQuery();
      if (!q.length) return;
      if (this.getAttribute('results') === 'true') this.open(); else this.getResults(q);
    }

    handleKeydown(evt) {
      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();
          if (this.hasAttribute('open')) this.moveSelection(evt.key === 'ArrowDown' ? 1 : -1);
          break;
        case 'Enter': {
          var selected = this.querySelector('.predictive-search__item[aria-selected="true"] > .js-search-link');
          if (selected && !selected.closest('.js-submit')) { evt.preventDefault(); selected.click(); }
          break;
        }
        case 'Escape': this.close(); break;
        default: break;
      }
    }

    handleKeyup() { if (!this.getQuery().length) this.close(); }

    moveSelection(dir) {
      var items = Array.prototype.slice.call(this.querySelectorAll('.predictive-search__item'));
      if (!items.length) return;
      var current = items.findIndex(function (el) { return el.getAttribute('aria-selected') === 'true'; });
      var next = current === -1 ? (dir > 0 ? 0 : items.length - 1) : (current + dir + items.length) % items.length;
      items.forEach(function (el, i) { el.setAttribute('aria-selected', i === next ? 'true' : 'false'); });
      var el = items[next];
      if (!el.id) el.id = this.input.id + '-opt-' + next;
      this.input.setAttribute('aria-activedescendant', el.id);
      this.setLiveRegionText(el.textContent.trim().slice(0, 120));
      el.scrollIntoView({ block: 'nearest' });
    }

    handleResultClick(evt) {
      var link = evt.target.closest('.js-search-link');
      if (!link) return;
      if (link.classList.contains('predictive-search__view-all')) {
        // "View all": submit the form to /search
        evt.preventDefault();
        var form = this.querySelector('.search');
        if (form) form.submit();
        return;
      }
      var pid = link.getAttribute('data-product-id');
      if (pid) S.logClick(this.currentQid, pid, parseInt(link.getAttribute('data-position') || '0', 10) || null, null);
    }

    // ------------------------------------------------------------ fetching

    getResults(q) {
      var self = this;
      var seq = ++this.seq;
      var typeFilter = this.productTypeInput && this.productTypeInput.value ? 'product_type:=`' + this.productTypeInput.value.replace(/`/g, '') + '`' : null;
      var key = (typeFilter || '') + '|' + q.toLowerCase();
      this.setLiveRegionText(this.loadingText);
      if (this.cache[key]) { this.render(this.cache[key], q); return; }

      // dropdown-size-1 (snippets/sw-exp.liquid): the "ten" arm shows 10 product rows instead of 6.
      var exp = window.swExp;
      var rows = exp && exp.running('dropdown-size-1') && exp.arm('dropdown-size-1') === 'ten' ? 10 : Math.min(cfg.pSearchLimit || 6, 6);
      S.search(q, { per_page: rows, filters: typeFilter ? [typeFilter] : [], collections_per_page: 6, suggestions: true, timeoutMs: Math.max(cfg.timeoutMs || 300, 600) })
        .then(function (res) {
          if (seq !== self.seq) return;
          var html = self.buildHtml(res, q);
          self.cache[key] = html;
          self.render(html, q);
          self.setAttribute('data-sw-engine', 'typesense');
          self.removeAttribute('data-sw-fallback');
          self.currentQid = S.logQuery(q, res, 'dropdown', 'typesense');
          // both arms are exposed the first time the dropdown shows products
          if (exp && res && res.products && res.products.hits && res.products.hits.length) exp.expose('dropdown-size-1');
        })
        .catch(function (err) {
          if (seq !== self.seq) return;
          // which engine filled the dropdown and why, for qa/tests/search.spec.mjs (same as #sw-search-page)
          self.setAttribute('data-sw-engine', 'native');
          self.setAttribute('data-sw-fallback', String(err ? (err.name && err.name !== 'Error' ? err.name + ': ' : '') + (err.message || err) : 'unknown').slice(0, 160));
          self.fallback(q, seq);
        });
    }

    /** Shopify native predictive search section (same request the theme's predictive-search.js makes). */
    fallback(q, seq) {
      var self = this;
      var fields = 'title,product_type,variants.title,vendor' + (cfg.pSearchIncludeSkus ? ',variants.sku' : '') + (cfg.pSearchIncludeTags ? ',tag' : '');
      var qParam = this.productTypeInput && this.productTypeInput.value ? 'product_type:' + this.productTypeInput.value + ' AND ' + q : q;
      var params = 'q=' + encodeURIComponent(qParam) + '&' + encodeURIComponent('resources[limit]') + '=' + (cfg.pSearchLimit || 10) +
        '&' + encodeURIComponent('resources[limit_scope]') + '=each&' + encodeURIComponent('resources[options][fields]') + '=' + fields + '&section_id=predictive-search';
      fetch((cfg.predictiveSearchUrl || '/search/suggest') + '?' + params).then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      }).then(function (text) {
        if (seq !== self.seq) return;
        var tmpl = document.createElement('template');
        tmpl.innerHTML = text;
        var el = tmpl.content.querySelector('#shopify-section-predictive-search');
        var markup = el ? el.innerHTML.replace(/psearch/g, self.input.id) : '';
        self.render(markup, q);
        self.currentQid = S.logQuery(q, null, 'dropdown', 'native');
      }).catch(function () { self.close(); });
    }

    // ------------------------------------------------------------ rendering

    buildHtml(res, q) {
      var products = (res.products && res.products.hits) || [];
      var collections = (res.collections && res.collections.hits) || [];
      var pages = (res.pages && res.pages.hits) || [];
      var found = res.found || 0;
      var a = res.analysis;
      var out = '<div class="predictive-search sw-dropdown absolute bg-theme-bg text-theme-text text-start"><div class="predictive-search__scroller overflow-auto"><div class="predictive-search__results flex flex-col">';

      // query completions first (query log, collection titles, brands), like the leaders' autocomplete
      var completions = S.completions(res, 4);
      if (completions.length && products.length) {
        out += '<ul class="predictive-search__list sw-dropdown__group sw-dropdown__group--suggestions" role="list">';
        completions.forEach(function (c) {
          out += '<li class="predictive-search__item" role="option"><a class="predictive-result predictive-result--suggestion sw-suggestion block focus-inset js-search-link" href="' + S.escapeHtml(c.url) + '"><span class="sw-suggestion__text">' + highlightPrefix(c.text, a.residual || a.normalized) + '</span></a></li>';
        });
        out += '</ul>';
      }

      // collection routing: a strong title match goes first
      var strongCollections = collections.filter(function (h) { return isStrongCollection(h, a); }).slice(0, 2);
      if (strongCollections.length) {
        out += '<ul class="predictive-search__list sw-dropdown__group" role="list"><li class="sw-dropdown__heading" aria-hidden="true">' + S.escapeHtml(str.collections || 'Collections') + '</li>';
        strongCollections.forEach(function (h) {
          var d = h.document;
          out += '<li class="predictive-search__item" role="option"><a class="predictive-result predictive-result--collection flex items-center focus-inset js-search-link" href="' + S.escapeHtml(d.url) + '">' +
            '<span class="predictive-result__info flex-auto"><span class="predictive-result__title h6 sw-dropdown__title">' + S.highlight(h, 'title', d.title) + '</span>' +
            (d.products_count ? '<span class="predictive-result__sub-title text-sm">' + d.products_count + ' products</span>' : '') + '</span></a></li>';
        });
        out += '</ul>';
      }

      if (products.length) {
        // one row per product: image | title (and the SKU when the query matched one) | price. No stock or Drop Shipped
        // label here: too busy for a quick look (Ryan 2026-09-23); status shows on cards and the PDP, as on Amazon
        out += '<ul class="predictive-search__list sw-dropdown__group" role="list"><li class="sw-dropdown__heading" aria-hidden="true">' + S.escapeHtml(str.products || 'Products') + '</li>';
        products.forEach(function (h, i) {
          var d = h.document;
          var img = S.imageUrl(d.image, 96);
          var sku = S.matchedSku(h);
          out += '<li class="predictive-search__item" role="option"><a class="predictive-result sw-row focus-inset js-search-link" href="' + S.escapeHtml(d.url) + '" data-product-id="' + S.escapeHtml(d.id) + '" data-position="' + (i + 1) + '">' +
            '<span class="sw-row__media">' + (img ? '<img src="' + S.escapeHtml(img) + '" alt="" loading="eager" width="48" height="48">' : '') + '</span>' +
            '<span class="sw-row__main"><span class="predictive-result__title sw-dropdown__title">' + S.highlight(h, 'title', d.title) + '</span>' +
            (sku ? '<span class="sw-dropdown__sku">' + sku + '</span>' : '') + '</span>' +
            '<span class="sw-row__side">' + S.priceHtml(d) + '</span>' +
            '</a></li>';
        });
        var allLabel = found > products.length ? 'See all ' + found.toLocaleString('en-US') + ' results for' : 'See all results for';
        out += '</ul><div class="predictive-search__item sw-dropdown__footer js-submit" role="option"><button type="submit" class="predictive-search__view-all sw-dropdown__all w-full text-start focus-inset js-search-link">' +
          '<span>' + S.escapeHtml(allLabel) + ' <strong>“' + S.escapeHtml(q) + '”</strong></span><span aria-hidden="true">→</span></button></div>';
      }

      if (pages.length && a.intent === 'page') {
        out += '<ul class="predictive-search__list sw-dropdown__group" role="list"><li class="sw-dropdown__heading" aria-hidden="true">' + S.escapeHtml(str.pages || 'Pages') + '</li>';
        pages.forEach(function (h) {
          var d = h.document;
          out += '<li class="predictive-search__item" role="option"><a class="predictive-result flex items-start focus-inset js-search-link" href="' + S.escapeHtml(d.url) + '"><div class="predictive-result__info flex-auto"><h3 class="predictive-result__title h6 sw-dropdown__title">' + S.highlight(h, 'title', d.title) + '</h3></div></a></li>';
        });
        out += '</ul>';
      }

      if (!products.length && !strongCollections.length && !pages.length) {
        out += '<div class="predictive-search__no-results">' + S.escapeHtml((str.noResults || 'No results for') + ' “' + q + '”') + '</div>';
        var hubs = collections.slice(0, 3);
        if (hubs.length) {
          out += '<ul class="predictive-search__list sw-dropdown__group" role="list"><li class="sw-dropdown__heading" aria-hidden="true">' + S.escapeHtml(str.tryHubs || 'Browse these collections') + '</li>';
          hubs.forEach(function (h) {
            out += '<li class="predictive-search__item" role="option"><a class="predictive-result flex items-center focus-inset js-search-link" href="' + S.escapeHtml(h.document.url) + '"><span class="predictive-result__info flex-auto"><span class="predictive-result__title h6 sw-dropdown__title">' + S.escapeHtml(h.document.title) + '</span></span></a></li>';
          });
          out += '</ul>';
        }
      }
      out += '</div></div></div>';
      return out;
    }

    render(markup, q) {
      this.results.innerHTML = markup;
      this.setAttribute('results', 'true');
      this.open();
      this.setLiveRegionText('');
    }

    setLiveRegionText(text) {
      if (!this.statusEl) return;
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = text;
      var el = this.statusEl;
      setTimeout(function () { el.setAttribute('aria-hidden', 'true'); }, 1000);
    }

    open() {
      if (this.overlay) this.overlay.classList.add('is-visible');
      if (this.getQuery().length && this.resetBtn) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.closeHandler = this.closeHandler || this.close.bind(this);
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      if (this.resetBtn) this.resetBtn.addEventListener('click', this.closeHandler);
      if (this.overlay) this.overlay.addEventListener('click', this.closeHandler);
    }

    close(evt) {
      if (evt && this.resetBtn && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }
      var selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');
      this.removeAttribute('open');
      if (this.resetBtn) this.resetBtn.hidden = true;
      if (this.overlay) this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');
      this.removeEventListener('keydown', this.keydownHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      if (this.resetBtn) this.resetBtn.removeEventListener('click', this.closeHandler);
      if (this.overlay) this.overlay.removeEventListener('click', this.closeHandler);
    }
  }

  /** Bold the part of a completion the buyer has not typed yet (escaped). */
  function highlightPrefix(text, typed) {
    var t = String(text), lower = t.toLowerCase(), idx = typed ? lower.indexOf(typed.toLowerCase()) : -1;
    if (idx === -1) return '<span class="sw-suggestion__rest">' + S.escapeHtml(t) + '</span>';
    return S.escapeHtml(t.slice(0, idx)) + '<span class="sw-suggestion__typed">' + S.escapeHtml(t.slice(idx, idx + typed.length)) + '</span><span class="sw-suggestion__rest">' + S.escapeHtml(t.slice(idx + typed.length)) + '</span>';
  }

  /** A collection is offered first only when the query is (nearly) its title, not on any partial token match. */
  function isStrongCollection(hit, a) {
    var title = String(hit.document.title || '').toLowerCase().replace(/\s+/g, ' ').trim();
    var q = a.residual || a.normalized;
    if (!q) return false;
    if (title === q) return true;
    var qt = q.split(' '), tt = title.split(' ');
    var covered = qt.filter(function (t) { return tt.indexOf(t) !== -1; }).length;
    return qt.length >= 1 && covered === qt.length && tt.length <= qt.length + 1;
  }

  customElements.define('predictive-search', SWPredictiveSearch);
})();
